import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PaymentButton from "@/components/PaymentButton";
import PayPalPayButton from "@/components/PayPalPayButton";
import { Check } from "lucide-react";

const MEMBERSHIP_FEE = "$25.00 USD / year";

const benefits = [
    "Priority access & discounts to all ZSGI programs and summits",
    "Invitations to members-only briefings and youth councils",
    "A verified ZSGI membership certificate",
    "Early access to fellowships, ambassador openings and events",
    "Quarterly impact digest from across the global network",
];

export default function MembershipPage() {
    const [form, setForm] = useState({ name: "", email: "", phone: "" });
    const [paid, setPaid] = useState(false);
    const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const ready = form.name && form.email;

    return (
        <section data-testid="membership-page" className="min-h-screen pt-32 md:pt-40 pb-24 md:pb-40">
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
                <div className="grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-6">
                        <div className="label-mono">— ZSGI MEMBERSHIP</div>
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
                            className="serif-display text-5xl md:text-7xl mt-4 leading-[0.95]"
                        >
                            Join the <span className="serif-italic text-[var(--gold)]">inner circle.</span>
                        </motion.h1>
                        <p className="mt-8 text-lg text-[var(--ivory)]/80 max-w-md">
                            Become a member of Z Ed School of Global Impact — and stand inside a global movement of youth-led changemakers.
                        </p>
                        <div className="mt-10 serif-display text-4xl text-[var(--gold)]">{MEMBERSHIP_FEE}</div>
                        <ul className="mt-8 space-y-3">
                            {benefits.map((b) => (
                                <li key={b} className="flex items-start gap-3 text-[var(--ivory)]/80">
                                    <Check className="w-4 h-4 mt-1 text-[var(--emerald)] shrink-0" /> {b}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-5 md:col-start-8">
                        <div className="border border-[rgba(255,255,240,0.2)] p-8 md:p-10">
                            {paid ? (
                                <div data-testid="membership-success">
                                    <div className="label-mono text-[var(--emerald)]">— WELCOME, MEMBER</div>
                                    <h2 className="serif-display text-3xl mt-4">You're in, {form.name.split(" ")[0]}.</h2>
                                    <p className="mt-4 text-[var(--ivory)]/80">A membership confirmation and receipt are on their way to {form.email}.</p>
                                    <Link to="/" className="btn-ghost mt-8">Return home</Link>
                                </div>
                            ) : (
                                <>
                                    <div className="label-mono text-[var(--gold)] mb-6">— BECOME A MEMBER</div>
                                    <div className="space-y-6">
                                        <div>
                                            <label className="label-mono block mb-2">Full name</label>
                                            <input className="input-editorial" value={form.name} onChange={change("name")} data-testid="membership-name" />
                                        </div>
                                        <div>
                                            <label className="label-mono block mb-2">Email</label>
                                            <input type="email" className="input-editorial" value={form.email} onChange={change("email")} data-testid="membership-email" />
                                        </div>
                                        <div>
                                            <label className="label-mono block mb-2">Phone (optional)</label>
                                            <input className="input-editorial" value={form.phone} onChange={change("phone")} data-testid="membership-phone" />
                                        </div>
                                    </div>
                                    <div className="mt-8 space-y-4">
                                        <PaymentButton
                                            purpose="membership"
                                            item="membership"
                                            name={form.name}
                                            email={form.email}
                                            phone={form.phone}
                                            label={`Pay ${MEMBERSHIP_FEE}`}
                                            className={`w-full justify-center ${ready ? "btn-gold" : "btn-ghost opacity-60 pointer-events-none"}`}
                                            testid="membership-pay"
                                            onPaid={() => setPaid(true)}
                                        />
                                        <div className="flex items-center gap-3 label-mono text-[var(--ivory)]/40">
                                            <span className="flex-1 h-px bg-[rgba(255,255,240,0.15)]" /> OR <span className="flex-1 h-px bg-[rgba(255,255,240,0.15)]" />
                                        </div>
                                        <PayPalPayButton
                                            purpose="membership"
                                            item="membership"
                                            name={form.name}
                                            email={form.email}
                                            phone={form.phone}
                                            disabled={!ready}
                                            onPaid={() => setPaid(true)}
                                        />
                                        <p className="text-xs text-[var(--ivory)]/50 label-mono">Secure payment via Razorpay or PayPal.</p>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
